import logger from '../../Utils/logger';
import React, { useCallback, useEffect, useRef, useState } from "react";
import styles from "./Style.module.css";
import { isValidPhoneNumber } from "react-phone-number-input";
import { useVoucherContext } from "../../Context/Voucher/VoucherContext.js";
import { useNavigate } from "react-router-dom";
import { useCreateVoucherOrder } from "../../APICalling/useApiQueries.js";
import { getErrorMessage } from "../../Utils/getErrorMessage.js";
import ToggleSwitch from "../../Components/ToggleSwitch/ToggleSwitch.js";
import LoaderMain from "../../Components/Loader/LoaderMain.js";
import { useSticky } from "../../Utils/Helper.js";
import { Header } from "../../Components/Header/Header.js";
import Input from "../../Components/FormInputs/Input.js";
import NotesInput from "../../Components/FormInputs/NotesInput.js";
import VoucherCart from "../../Components/Cart/VoucherCart.js";
import VoucherDetails from "../../Components/VoucherDetails/VoucherDetails.js";
import { BtnPrimary } from "../../Components/Buttons/BtnPrimary.js";
import TermsAndConditionsV2 from "../../Components/TermsAndConditionsV2/TermsAndConditionsV2.js";
import { maxVoucherCount } from "../../Utils/voucherConfig.js";
import { Snakbar } from "../../Components/Snakbar/Snakbar.js";

const VOUCHER_SELECTION_ERROR = "Please select at least one voucher";

export const BillingDetails = () => {
  const {
    selectedVouchers,
    voucherTotal,
    voucherCount,
    updateVoucherQuantity,
    billingDetails,
    setBillingDetails,
    recipientDetails,
    setRecipientDetails,
    giftMessage,
    setGiftMessage,
    setOrderData,
    setOrderMessage,
  } = useVoucherContext();

  const [mainLoading, setMainLoading] = useState(true);
  const navigate = useNavigate();
  const [errors, setErrors] = useState({});

  // Redirect if no vouchers selected
  useEffect(() => {
    if (!selectedVouchers || selectedVouchers.length === 0) {
      navigate("/select-vouchers");
    }
  }, [selectedVouchers, navigate]);

  const createVoucherOrderMutation = useCreateVoucherOrder({
    onSuccess: (response) => {
      const orderInfo = response?.data?.data;
      const message = response?.data?.message;
      logger.log("✅ Order created successfully - orderInfo:", orderInfo);
      if (orderInfo) {
        setOrderData(orderInfo);
      }
      if (message) {
        setOrderMessage(message);
        logger.log("✅ Order message:", message);
      }
      navigate("/review-voucher-purchase");
    },
    onError: (error) => {
      logger.error("Error creating voucher order:", error);
      setErrors((prev) => ({
        ...prev,
        error: getErrorMessage(error?.response?.data),
      }));
    },
  });

  const isSubmitting = createVoucherOrderMutation.isPending;
  const [isAgreeTerms] = useState(true);
  const [isSubscribed, setIsSubscribed] = useState(1);
  const subscribedNewsletter = () => setIsSubscribed((prev) => Number(!prev));
  const { isSticky, sentinelRef } = useSticky();
  const [giftMessageError, setGiftMessageError] = useState("");
  const [voucherSelectionError, setVoucherSelectionError] = useState("");

  const billingNameRef = useRef(null);
  const billingMobileRef = useRef(null);
  const billingEmailRef = useRef(null);
  const recipientNameRef = useRef(null);
  const recipientMobileRef = useRef(null);
  const recipientEmailRef = useRef(null);
  const termsRef = useRef(null);
  const voucherSelectionRef = useRef(null);

  const scrollToErrorField = (fieldName) => {
    const fieldMap = {
      billing_name: billingNameRef,
      billing_mobile: billingMobileRef,
      billing_email: billingEmailRef,
      recipient_name: recipientNameRef,
      recipient_mobile: recipientMobileRef,
      recipient_email: recipientEmailRef,
      agreeTerms: termsRef,
      voucher_selection: voucherSelectionRef,
    };

    const targetRef = fieldMap[fieldName];
    if (!targetRef?.current) return;

    targetRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    const focusableEl = targetRef.current.querySelector("input, textarea, select");
    focusableEl?.focus();
  };

  const handleGiftMessageChange = (e) => {
    const value = e.target.value;
    if (value.length > 250) {
      setGiftMessage(value.slice(0, 250));
      setGiftMessageError("Maximum 250 characters allowed.");
      return;
    }
    setGiftMessage(value);
    setGiftMessageError("");
  };

  const validateField = (fieldKey, value, label) => {
    let error = "";
    const normalizedValue = typeof value === "string" ? value.trim() : value;
    const isValidName = /^[\p{L}\d\s]*$/u.test(value || "");

    if (fieldKey.endsWith("_name")) {
      if (!normalizedValue) error = `${label} is required`;
      else if (!isValidName)
        error = `${label} can contain only letters, numbers and spaces`;
    }

    if (fieldKey.endsWith("_email")) {
      if (!normalizedValue) error = `${label} is required`;
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value || ""))
        error = "Invalid email format";
    }

    if (fieldKey.endsWith("_mobile")) {
      if (!value) error = `${label} is required`;
      else if (!isValidPhoneNumber(value)) error = "Invalid phone number";
    }

    setErrors((prevErrors) => ({
      ...prevErrors,
      [fieldKey]: error,
      error: "",
    }));

    return error === "";
  };

  const handleNameChange = (section) => (e) => {
    const { value } = e.target;
    if (!/^[\p{L}\d\s]*$/u.test(value)) return;

    if (section === "billing") {
      setBillingDetails((prev) => ({ ...prev, name: value }));
      validateField("billing_name", value, "Billing Name");
      return;
    }

    setRecipientDetails((prev) => ({ ...prev, name: value }));
    validateField("recipient_name", value, "Recipient Name");
  };

  const handleEmailChange = (section) => (e) => {
    const { value } = e.target;

    if (section === "billing") {
      setBillingDetails((prev) => ({ ...prev, email: value }));
      validateField("billing_email", value, "Billing Email");
      return;
    }

    setRecipientDetails((prev) => ({ ...prev, email: value }));
    validateField("recipient_email", value, "Recipient Email");
  };

  const handlePhoneChange = (section) => (value) => {
    if (section === "billing") {
      setBillingDetails((prev) => ({ ...prev, mobile: value || "" }));
      validateField("billing_mobile", value, "Billing Contact");
      return;
    }

    setRecipientDetails((prev) => ({ ...prev, mobile: value || "" }));
    validateField("recipient_mobile", value, "Recipient Contact");
  };

  const validateForm = () => {
    const billingNameValid = validateField(
      "billing_name",
      billingDetails.name,
      "Billing Name"
    );
    const billingMobileValid = validateField(
      "billing_mobile",
      billingDetails.mobile,
      "Billing Contact"
    );
    const billingEmailValid = validateField(
      "billing_email",
      billingDetails.email,
      "Billing Email"
    );
    const recipientNameValid = validateField(
      "recipient_name",
      recipientDetails.name,
      "Recipient Name"
    );
    const recipientMobileValid = validateField(
      "recipient_mobile",
      recipientDetails.mobile,
      "Recipient Contact"
    );
    const recipientEmailValid = validateField(
      "recipient_email",
      recipientDetails.email,
      "Recipient Email"
    );

    if (!isAgreeTerms) {
      setErrors((prev) => ({
        ...prev,
        agreeTerms: "You must agree to the Terms and Conditions",
      }));
      return { isValid: false, firstInvalidField: "agreeTerms" };
    }

    setErrors((prev) => ({ ...prev, agreeTerms: "" }));

    const firstInvalidField =
      (!billingNameValid && "billing_name") ||
      (!billingMobileValid && "billing_mobile") ||
      (!billingEmailValid && "billing_email") ||
      (!recipientNameValid && "recipient_name") ||
      (!recipientMobileValid && "recipient_mobile") ||
      (!recipientEmailValid && "recipient_email") ||
      null;

    return {
      isValid:
        billingNameValid &&
        billingMobileValid &&
        billingEmailValid &&
        recipientNameValid &&
        recipientMobileValid &&
        recipientEmailValid &&
        isAgreeTerms,
      firstInvalidField,
    };
  };

  const handleSubmit = (e) => {
    e?.preventDefault?.();
    if (isSubmitting) return;

    const { isValid, firstInvalidField } = validateForm();
    if (!isValid) {
      if (firstInvalidField) scrollToErrorField(firstInvalidField);
      return;
    }

    setErrors((prev) => ({ ...prev, error: "" }));

    if (selectedVouchers.length === 0) {
      setVoucherSelectionError(VOUCHER_SELECTION_ERROR);
      scrollToErrorField("voucher_selection");
      return;
    }

    // Consolidate items: for each product_id, use the final (highest) count value
    const consolidateItems = (vouchers) => {
      const itemMap = {};
      vouchers.forEach((voucher) => {
        const productId = voucher.id;
        const quantity = voucher.count || 1;
        // Use the maximum quantity (final state) for this product_id
        if (!itemMap[productId] || quantity > itemMap[productId].quantity) {
          itemMap[productId] = {
            product_id: productId,
            quantity: quantity,
          };
        }
      });
      return Object.values(itemMap);
    };

    const voucherOrderData = {
      items: consolidateItems(selectedVouchers),
      buyer_name: billingDetails.name,
      buyer_email: billingDetails.email,
      buyer_phone: billingDetails.mobile,
      recipient_name: recipientDetails.name,
      recipient_email: recipientDetails.email,
      recipient_phone: recipientDetails.mobile,
      message: giftMessage || "",
      billing_details: {
        address_line_1: "",
        city: "",
        postal_code: "",
      },
      payment_method: "adyen",
    };

    createVoucherOrderMutation.mutate(voucherOrderData);
  };

  useEffect(() => {
    const loaderTimer = setTimeout(() => setMainLoading(false), 500);
    return () => clearTimeout(loaderTimer);
  }, []);

  useEffect(() => {
    if (errors.error && errors.error !== VOUCHER_SELECTION_ERROR) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [errors.error]);

  const handleIncrementVoucher = useCallback(
    (voucher) => {
      if (!voucher?.id) return;
      if (Number(voucherCount) >= maxVoucherCount) return;
      const nextCount = Math.max(0, Number(voucher.count) || 0) + 1;
      updateVoucherQuantity(voucher, nextCount);
    },
    [updateVoucherQuantity, voucherCount]
  );

  const handleDecrementVoucher = useCallback(
    (voucher) => {
      if (!voucher?.id) return;
      const nextCount = Math.max(0, (Number(voucher.count) || 0) - 1);
      updateVoucherQuantity(voucher, nextCount);
    },
    [updateVoucherQuantity]
  );

  const handleSelectVouchers = (category) => {
    const categoryId = category?.category_id;
    navigate("/select-vouchers", {
      state:
        categoryId != null
          ? { scrollToCategoryId: categoryId }
          : undefined,
    });
  };

  return (
    <>
      {mainLoading && <LoaderMain />}
      <div className="sticky-sentinel" ref={sentinelRef} aria-hidden="true" />
      <div className={`sticky-header ${isSticky ? "is-sticky" : ""}`}>
        <Header title="Billing Details" />
      </div>

      <div className={`${styles.ContactDetailPage} space-y`}>
        <div className={styles.userDetailCard}>
          <form onSubmit={handleSubmit}>
            <div className={`${styles.ContactDetail} space-x container-md`}>
              <Input
                inputRef={billingNameRef}
                label="Billing Name"
                required={true}
                name="name"
                value={billingDetails.name}
                onChange={handleNameChange("billing")}
                error={errors.billing_name}
                wrapperClassName={styles.formGroup}
                labelClassName={styles.formLabel}
              />

              <Input
                inputRef={billingMobileRef}
                label="Billing Contact"
                required={true}
                value={billingDetails.mobile}
                onChange={handlePhoneChange("billing")}
                error={errors.billing_mobile}
                placeholder="Enter phone number"
                isPhone={true}
                international={false}
                wrapperClassName={styles.formGroup}
                labelClassName={styles.formLabel}
              />

              <Input
                inputRef={billingEmailRef}
                label="Billing Email"
                required={true}
                type="email"
                name="email"
                value={billingDetails.email}
                onChange={handleEmailChange("billing")}
                error={errors.billing_email}
                wrapperClassName={styles.formGroup}
                labelClassName={styles.formLabel}
              />

              <div className={styles.termsAndConditions}>
                <div className={styles.termsAndConditionsInner}>
                  <p>Stay up to date with Natureland’s products, services and offers.</p>
                  <ToggleSwitch
                    name="subscribe"
                    checked={isSubscribed}
                    handleChange={subscribedNewsletter}
                  />
                </div>
              </div>

              <div className={styles.hr} />

              <div className={styles.recipientDetails}>
                <h2 className="title-main">Recipient Details</h2>
                <br />
                <Input
                  inputRef={recipientNameRef}
                  label="Recipient Name"
                  required={true}
                  name="name"
                  value={recipientDetails.name}
                  onChange={handleNameChange("recipient")}
                  error={errors.recipient_name}
                  wrapperClassName={styles.formGroup}
                  labelClassName={styles.formLabel}
                />

                <Input
                  inputRef={recipientMobileRef}
                  label="Recipient Contact"
                  required={true}
                  value={recipientDetails.mobile}
                  onChange={handlePhoneChange("recipient")}
                  error={errors.recipient_mobile}
                  placeholder="Enter phone number"
                  isPhone={true}
                  international={false}
                  wrapperClassName={styles.formGroup}
                  labelClassName={styles.formLabel}
                />

                <Input
                  inputRef={recipientEmailRef}
                  label="Recipient Email"
                  required={true}
                  type="email"
                  name="email"
                  value={recipientDetails.email}
                  onChange={handleEmailChange("recipient")}
                  error={errors.recipient_email}
                  warning={recipientDetails.email && !errors.recipient_email ? "Please verify the email is correct. We will send the voucher to this email." : ""}
                  wrapperClassName={styles.formGroup}
                  labelClassName={styles.formLabel}
                />
              </div>

              <NotesInput
                name="gift_message"
                label="Gift Message"
                optionalText="(Optional)"
                value={giftMessage}
                onChange={handleGiftMessageChange}
                error={giftMessageError}
                placeholder="Wishing you a fantastic birthday filled with joy, laughter, and unforgettable moments! Have an amazing day!"
                maxLength={250}
                wrapperClassName={styles.notes}
                formGroupClassName={styles.formGroup}
                labelClassName={styles.formLabel}
                countClassName={styles.notesLength}
              />
            </div>
            <div className={styles.hr} />

            <div className={styles.SelectedVoucherDetails}>
              <div className="container space-x">
                <h2 className="title-main space-b">Voucher Details</h2>
                {selectedVouchers.length > 0 ? (
                <VoucherDetails
                  selectedVouchers={selectedVouchers}
                  onIncrementVoucher={handleIncrementVoucher}
                  onDecrementVoucher={handleDecrementVoucher}
                  onEditCategory={handleSelectVouchers}
                  totalSelectedCount={voucherCount}
                />
                ) : (
                  <div ref={voucherSelectionRef}>
                    <div className={styles.noVouchers}>No vouchers selected</div>
                    <div className="error-msg">{voucherSelectionError}</div>
                    <BtnPrimary
                      label="Select Vouchers"
                      customClass={styles.selectVouchersBtn}
                      handleClick={() => handleSelectVouchers()}
                    />
                  </div>
                )}
              </div>
            </div>

            <div className={styles.hr} />
            
            <div ref={termsRef} className="container space-x">
              <TermsAndConditionsV2 />
            </div>

            {/* {errors.error ? (
              <div className={styles.errorContainer}>
                <div className={styles.errorMessage}>{errors.error}</div>
              </div>
            ) : null} */}

 <Snakbar
          text={errors.error}
          active={errors.error}
          varient="danger"
          type="error"
          hasButton={true}
          onClose={() => setErrors((prev) => ({ ...prev, error: "" }))}
          />


          <VoucherCart
              btnLabel={
                isSubmitting ? "Processing..." : `Next`
              }
              btnClick={handleSubmit}
              disabled={isSubmitting}
              isCartActive={true}
              displayTotal={voucherTotal}
              displayCount={voucherCount}
              />

            
          </form>
        </div>
      </div>
    </>
  );
};
